import {
  IScheduleHandler,
  ScheduleContext,
  ScheduleEvaluationResult,
} from "../types/index";
import {
  Playlist,
  ScheduleType,
  PrayerWindowSchedule,
  isPrayerWindowSchedule,
} from "../types";
import { getPrayerLength } from "../utils/prayerTimeUtils";

/**
 * Prayer Window Schedule Handler
 *
 * Handles playlists with ScheduleType.PrayerWindow (2)
 * Active between two prayer times on selected days of the week.
 *
 * Example: Between Fajr and Dhuhr on weekdays
 */
export class PrayerWindowScheduleHandler implements IScheduleHandler {
  public readonly scheduleType = ScheduleType.PrayerWindow;

  /**
   * Evaluate if playlist is active based on prayer window
   */
  evaluate(
    playlist: Playlist,
    context: ScheduleContext
  ): ScheduleEvaluationResult {
    const schedule = playlist.schedule;

    if (!schedule || !isPrayerWindowSchedule(schedule)) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: "Invalid or missing PrayerWindow schedule configuration",
      };
    }

    if (!context.prayerTimes) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: "Prayer times not available",
      };
    }

    const currentDay = context.currentTime.getDay();

    // Check if current day is in the schedule
    if (!schedule.daysOfWeek.includes(currentDay)) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: `Not active on ${this.getDayName(currentDay)}`,
      };
    }

    // Get prayer times
    const startPrayerTime = this.getPrayerTime(
      context.prayerTimes,
      schedule.startPrayer
    );
    const endPrayerTime = this.getPrayerTime(
      context.prayerTimes,
      schedule.endPrayer
    );

    if (!startPrayerTime || !endPrayerTime) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: `Could not resolve prayer times for ${schedule.startPrayer} or ${schedule.endPrayer}`,
      };
    }

    // Apply offsets to window start
    const windowStart = new Date(startPrayerTime);
    if (schedule.secondsAfterStartPrayerEnds) {
      const startPrayerLength = getPrayerLength(
        schedule.startPrayer,
        context.screenData
      );
      windowStart.setSeconds(
        windowStart.getSeconds() + startPrayerLength + schedule.secondsAfterStartPrayerEnds
      );
    } else if (schedule.secondsBeforeStartPrayer) {
      windowStart.setSeconds(
        windowStart.getSeconds() - schedule.secondsBeforeStartPrayer
      );
    }

    // Apply offsets to window end
    const endTime = new Date(endPrayerTime);
    if (schedule.secondsBeforeEndPrayer) {
      endTime.setSeconds(
        endTime.getSeconds() - schedule.secondsBeforeEndPrayer
      );
    } else {
      const prayerLength = getPrayerLength(
        schedule.endPrayer,
        context.screenData
      );
      endTime.setSeconds(endTime.getSeconds() + prayerLength);
    }

    // Check if current time is in window
    const isInWindow =
      context.currentTime >= windowStart && context.currentTime <= endTime;

    if (!isInWindow) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: `Outside prayer window (${schedule.startPrayer} to ${schedule.endPrayer})`,
        nextEvaluationTime:
          windowStart > context.currentTime ? windowStart : undefined,
      };
    }

    return {
      isActive: true,
      effectivePriority: playlist.priority,
      deactivationTime: endTime,
      reason: `Active between ${schedule.startPrayer} and ${schedule.endPrayer}`,
      nextEvaluationTime: endTime,
    };
  }

  /**
   * Get next state change time
   */
  getNextStateChange(
    playlist: Playlist,
    context: ScheduleContext
  ): Date | null {
    const schedule = playlist.schedule;

    if (
      !schedule ||
      !isPrayerWindowSchedule(schedule) ||
      !context.prayerTimes
    ) {
      return null;
    }

    const startPrayerTime = this.getPrayerTime(
      context.prayerTimes,
      schedule.startPrayer
    );
    const endPrayerTime = this.getPrayerTime(
      context.prayerTimes,
      schedule.endPrayer
    );

    if (!startPrayerTime || !endPrayerTime) {
      return null;
    }

    // Apply offsets to window start
    const windowStart = new Date(startPrayerTime);
    if (schedule.secondsAfterStartPrayerEnds) {
      const startPrayerLength = getPrayerLength(
        schedule.startPrayer,
        context.screenData
      );
      windowStart.setSeconds(
        windowStart.getSeconds() + startPrayerLength + schedule.secondsAfterStartPrayerEnds
      );
    } else if (schedule.secondsBeforeStartPrayer) {
      windowStart.setSeconds(
        windowStart.getSeconds() - schedule.secondsBeforeStartPrayer
      );
    }

    // Apply offsets to window end
    const endTime = new Date(endPrayerTime);
    if (schedule.secondsBeforeEndPrayer) {
      endTime.setSeconds(
        endTime.getSeconds() - schedule.secondsBeforeEndPrayer
      );
    } else {
      const prayerLength = getPrayerLength(
        schedule.endPrayer,
        context.screenData
      );
      endTime.setSeconds(endTime.getSeconds() + prayerLength);
    }

    // If before window, return start
    if (context.currentTime < windowStart) {
      return windowStart;
    }

    // If in window, return end
    if (context.currentTime >= windowStart && context.currentTime <= endTime) {
      return endTime;
    }

    // If after window, return next day's start (simplified - would need tomorrow's prayer times)
    return null;
  }

  /**
   * Validate configuration
   */
  validateConfiguration(playlist: Playlist): string[] {
    const errors: string[] = [];
    const schedule = playlist.schedule;

    if (!schedule || !isPrayerWindowSchedule(schedule)) {
      errors.push("PrayerWindow schedule configuration is missing or invalid");
      return errors;
    }

    const prayerWindowSchedule = schedule as PrayerWindowSchedule;

    // Validate schedule type
    if (prayerWindowSchedule.type !== ScheduleType.PrayerWindow) {
      errors.push(
        `Schedule type mismatch: expected ${ScheduleType.PrayerWindow}, got ${prayerWindowSchedule.type}`
      );
    }

    // Validate days of week
    if (!schedule.daysOfWeek || schedule.daysOfWeek.length === 0) {
      errors.push("At least one day of the week must be selected");
    }

    if (schedule.daysOfWeek.some((day) => day < 0 || day > 6)) {
      errors.push("Days of week must be between 0 (Sunday) and 6 (Saturday)");
    }

    // Validate prayer names
    if (!schedule.startPrayer) {
      errors.push("Start prayer is required");
    }

    if (!schedule.endPrayer) {
      errors.push("End prayer is required");
    }

    if (schedule.startPrayer === schedule.endPrayer) {
      errors.push("Start and end prayers must be different");
    }

    // Validate offsets
    if (
      schedule.secondsBeforeStartPrayer &&
      schedule.secondsBeforeStartPrayer < 0
    ) {
      errors.push("Seconds before start prayer cannot be negative");
    }

    return errors;
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  private getPrayerTime(prayerTimes: any, prayerName: string): Date | null {
    // Map prayer names to prayer times keys
    const mapping: Record<string, string> = {
      StartFajr: "BegFajr",
      JamFajr: "JamFajr",
      StartSunrise: "Sunrise",
      StartDhuhr: "BegDhuhr",
      JamDhuhr: "JamDhuhr",
      StartAsr: "BegAsar",
      JamAsar: "JamAsar",
      StartMaghrib: "BegMaghrib",
      JamMaghrib: "JamMaghrib",
      StartEsha: "BegEsha",
      JamEsha: "JamEsha",
      Jummah1: "Jummah1",
      Jummah2: "Jummah2",
      Jummah3: "Jummah3",
      Jummah4: "Jummah4",
    };

    const key = mapping[prayerName];
    if (!key || !prayerTimes[key]) {
      return null;
    }

    const timeStr = prayerTimes[key];
    if (!timeStr || typeof timeStr !== "string") {
      return null;
    }

    // Validate time string format (HH:MM)
    const timeParts = timeStr.split(":");
    if (timeParts.length !== 2) {
      return null;
    }

    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);

    // Validate hours and minutes are valid numbers and in range
    if (
      isNaN(hours) ||
      isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }

    try {
      // Parse time string to Date
      const today = new Date();
      const prayerDate = new Date(today);
      prayerDate.setHours(hours, minutes, 0, 0);

      // Additional validation: ensure the date was set correctly
      if (
        prayerDate.getHours() !== hours ||
        prayerDate.getMinutes() !== minutes
      ) {
        return null;
      }

      return prayerDate;
    } catch (error) {
      // If Date constructor or setHours fails, return null
      return null;
    }
  }

  private getDayName(day: number): string {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return days[day] || "Unknown";
  }
}
